import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Printer, RefreshCw, RotateCcw } from 'lucide-react';
import apiService from '../services/api';
import PosCaisseSharedHeader from '../components/caisse/PosCaisseSharedHeader';
import { usePosDisplayTimeZone } from '../hooks/usePosDisplayTimeZone';
import { createCaisseIdempotencyKey } from '../utils/caisseIdempotency';

function resolveCommercantId(searchParams) {
  const fromUrl = searchParams.get('commercantId');
  if (fromUrl) return fromUrl;
  try {
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    return user.commercantId || user.commercant?.id || null;
  } catch {
    return null;
  }
}

const REPRINTABLE = new Set(['TRANSMITTED', 'FAILED', 'UNCERTAIN', 'ACK_TECHNICAL']);

export default function CaissePrint() {
  const [searchParams] = useSearchParams();
  const commercantId = resolveCommercantId(searchParams);
  const posClock = usePosDisplayTimeZone();
  const [assignments, setAssignments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [supervision, setSupervision] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionJobId, setActionJobId] = useState(null);

  const reload = useCallback(async () => {
    if (!commercantId) return;
    setLoading(true);
    setError(null);
    try {
      const [a, j, s] = await Promise.all([
        apiService.listPrintStationAssignments(commercantId),
        apiService.listPrintJobs(commercantId, { limit: 30 }),
        apiService.getPrintSupervision(commercantId),
      ]);
      setAssignments(a.assignments || []);
      setJobs(j.jobs || []);
      setSupervision(s.supervision || null);
    } catch (e) {
      setError(e.message || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const runTest = async (hardwareDeviceId) => {
    const key = createCaisseIdempotencyKey('print-test');
    await apiService.sendPrintTestTicket({
      hardwareDeviceId,
      idempotencyKey: key,
      commercantId,
      processImmediately: true,
    });
    await reload();
  };

  const runReprint = async (jobId) => {
    setActionJobId(jobId);
    try {
      await apiService.reprintPrintJob(jobId, {
        idempotencyKey: createCaisseIdempotencyKey('print-reprint'),
        commercantId,
      });
      await reload();
    } catch (e) {
      setError(e.message || 'Réimpression impossible');
    } finally {
      setActionJobId(null);
    }
  };

  const runRequeue = async (jobId) => {
    setActionJobId(jobId);
    try {
      await apiService.requeuePrintJob(jobId, commercantId);
      await reload();
    } catch (e) {
      setError(e.message || 'Remise en file impossible');
    } finally {
      setActionJobId(null);
    }
  };

  const alerts = supervision?.alerts || [];
  const workerHint = supervision?.workerHint;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="print-config-root">
      <div className="-mx-4 md:-mx-8 -mt-4 md:-mt-8 mb-4">
        <PosCaisseSharedHeader commercant={posClock.commercant} />
      </div>
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={`/caisse/kitchen?commercantId=${commercantId || ''}`}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour KDS
          </Link>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
            <Printer className="h-5 w-5" />
            Impression cuisine / bar
          </h1>
          <button
            type="button"
            data-testid="print-config-refresh"
            onClick={reload}
            className="ml-auto inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </button>
        </div>

        {workerHint && !workerHint.envEnabled && !workerHint.syncMode ? (
          <p
            className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
            data-testid="print-worker-hint"
          >
            Traitement automatique : activer{' '}
            <code className="text-xs">POS_PRINT_WORKER_ENABLED=1</code> sur l&apos;API (ou{' '}
            <code className="text-xs">POS_PRINT_SYNC=1</code> en dev). Sans worker, les tickets restent
            en file jusqu&apos;à traitement serveur.
          </p>
        ) : null}

        {alerts.length > 0 ? (
          <ul className="space-y-2" data-testid="print-supervision-alerts">
            {alerts.map((a) => (
              <li
                key={a.code}
                className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <strong>{a.code}</strong> — {a.message}
                  {a.count != null ? ` (${a.count})` : ''}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
            {error}
          </p>
        )}

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-medium text-slate-700">Imprimantes par poste</h2>
          {loading ? (
            <p className="text-sm text-slate-500">Chargement…</p>
          ) : assignments.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="print-assignments-empty">
              Aucune affectation. Configurez le matériel depuis le cockpit commerçant (onglet
              Impression), puis associez chaque imprimante à un poste cuisine/bar via l&apos;API ou
              le support Clickorico.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="print-assignments-list">
              {assignments.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm"
                >
                  <span>
                    {row.preparationStation?.label || row.preparationStationId}
                    {' → '}
                    {row.hardwareDevice?.label || row.hardwareDeviceId}
                    {row.hardwareDevice?.provider ? ` (${row.hardwareDevice.provider})` : ''}
                    {row.hardwareDevice?.printEnabled === false ? ' — désactivée' : ''}
                  </span>
                  <button
                    type="button"
                    data-testid={`print-test-${row.hardwareDeviceId}`}
                    className="rounded-md bg-slate-900 px-3 py-1.5 text-xs text-white disabled:opacity-50"
                    disabled={!row.hardwareDevice?.printEnabled}
                    onClick={() => runTest(row.hardwareDeviceId)}
                  >
                    Ticket test
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-medium text-slate-700">Derniers travaux</h2>
          <p className="mb-3 text-xs text-slate-500">
            TRANSMITTED = transmis au connecteur · UNCERTAIN = papier non confirmé · réimpression =
            nouveau job (idempotent).
          </p>
          <ul className="space-y-2" data-testid="print-jobs-list">
            {jobs.map((job) => (
              <li key={job.id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <span className="font-medium">{job.documentKind}</span>
                    {' — '}
                    <span data-testid={`print-job-status-${job.id}`}>{job.status}</span>
                    {job.sourceKind === 'REPRINT' ? ' (réimpression)' : ''}
                  </span>
                  <span className="flex gap-2">
                    {job.status === 'FAILED' ? (
                      <button
                        type="button"
                        data-testid={`print-requeue-${job.id}`}
                        disabled={actionJobId === job.id}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs"
                        onClick={() => runRequeue(job.id)}
                      >
                        <RotateCcw className="h-3 w-3" />
                        Remettre en file
                      </button>
                    ) : null}
                    {REPRINTABLE.has(job.status) ? (
                      <button
                        type="button"
                        data-testid={`print-reprint-${job.id}`}
                        disabled={actionJobId === job.id}
                        className="rounded-md bg-slate-800 px-2 py-1 text-xs text-white disabled:opacity-50"
                        onClick={() => runReprint(job.id)}
                      >
                        Réimprimer
                      </button>
                    ) : null}
                  </span>
                </div>
                {job.lastError ? (
                  <span className="mt-1 block text-xs text-red-700">{job.lastError}</span>
                ) : null}
              </li>
            ))}
            {!loading && jobs.length === 0 ? (
              <li className="text-sm text-slate-500">Aucun travail récent.</li>
            ) : null}
          </ul>
        </section>
      </div>
    </div>
  );
}
